// NoteHistory - Collapsible note history display for SOP tasks

import {
  Box,
  VStack,
  Text,
  Link,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Badge,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { MdImage, MdVideocam, MdAudioFile, MdDescription, MdAccessTime } from 'react-icons/md';
import DOMPurify from 'dompurify';
import type { TaskNote } from '@/lib/sdk/chatbot/types';
import { formatters } from '@/client/utils/formatters';

interface NoteHistoryProps {
  notes: TaskNote[];
}

/**
 * Displays collapsible note history for SOP tasks
 * Filters out action notes (is_action: true) and custom data notes (is_custom_data: true)
 */
export function NoteHistory({ notes }: NoteHistoryProps) {
  // Filter out action notes and custom data notes (choices)
  const userNotes = notes.filter(note => !note.is_action && !note.is_custom_data);

  if (userNotes.length === 0) {
    return null;
  }

  // Sort by updated_at descending (most recent first)
  const sortedNotes = [...userNotes].sort((a, b) =>
    new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
  );

  const latestNote = sortedNotes[0];
  const olderNotes = sortedNotes.slice(1);

  const renderMediaIndicator = (note: TaskNote) => {
    if (!note.media_url) return null;

    const mimeType = note.mime_type?.toLowerCase() || '';
    let IconComponent = MdDescription;
    let label = 'File';

    if (mimeType.startsWith('image/')) {
      IconComponent = MdImage;
      label = 'Photo';
    } else if (mimeType.startsWith('video/')) {
      IconComponent = MdVideocam;
      label = 'Video';
    } else if (mimeType.startsWith('audio/')) {
      IconComponent = MdAudioFile;
      label = 'Audio';
    }

    return (
      <HStack spacing={1} mt={1}>
        <Icon as={IconComponent} color="text-muted" boxSize={4} />
        <Link
          href={note.media_url}
          isExternal
          fontSize="xs"
          color="brand.500"
        >
          {note.file_name || label}
        </Link>
      </HStack>
    );
  };

  const renderNote = (note: TaskNote) => (
    <Box
      p={3}
      bg="bg-surface"
      borderRadius="md"
      borderWidth="1px"
      borderColor="border-default"
    >
      {note.status_text && (
        <Box
          fontSize="sm"
          color="text-primary"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.status_text) }}
          sx={{
            'ul, ol': { paddingLeft: '1.5em', marginBottom: '0.25em' },
            'li': { marginBottom: '0.15em' },
            'strong': { fontWeight: 'bold' },
            'em': { fontStyle: 'italic' },
            'p': { margin: 0, marginBottom: '0.3em' },
            'p:last-child': { marginBottom: 0 },
          }}
        />
      )}
      {renderMediaIndicator(note)}
      {note.updated_at && (
        <HStack spacing={1} mt={2}>
          <Icon as={MdAccessTime} color="text-muted" boxSize={3} />
          <Text fontSize="xs" color="text-muted">
            {formatters.relativeTime(note.updated_at)}
          </Text>
        </HStack>
      )}
    </Box>
  );

  return (
    <Box>
      <HStack justify="space-between" mb={2}>
        <Text fontSize="sm" fontWeight="medium" color="text-secondary">
          Previous Responses
        </Text>
        <Badge colorScheme="blue" fontSize="xs">
          {userNotes.length} {userNotes.length === 1 ? 'entry' : 'entries'}
        </Badge>
      </HStack>

      {/* Latest note always visible */}
      <Box mb={olderNotes.length > 0 ? 2 : 0}>
        {renderNote(latestNote)}
      </Box>

      {/* Older notes in collapsible accordion */}
      {olderNotes.length > 0 && (
        <Accordion allowToggle>
          <AccordionItem border="none">
            <AccordionButton
              px={0}
              py={2}
              _hover={{ bg: 'transparent' }}
            >
              <Text fontSize="sm" color="text-muted" flex="1" textAlign="left">
                Show {olderNotes.length} older {olderNotes.length === 1 ? 'response' : 'responses'}
              </Text>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={0} pb={0}>
              <VStack spacing={2} align="stretch">
                {olderNotes.map((note) => (
                  <Box key={note.id}>
                    {renderNote(note)}
                  </Box>
                ))}
              </VStack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}
    </Box>
  );
}